import React, { useContext, useEffect, useMemo, useState } from 'react';
import { doc, updateDoc } from 'firebase/firestore';
import { Link, Navigate } from 'react-router-dom';
import { db } from '../firebase';
import Header from './Header';
import Footer from './Footer';
import ProfileImageUpload from './ProfileImageUpload';
import UserProPanel from './UserProPanel';
import { AuthContext } from '../context/AuthContext';
import { ADMIN_EMAIL } from '../services/auth';
import { useI18n } from '../i18n/I18nContext';
import { clientDashboardCopy, pickPageCopy } from '../i18n/pageCopy';
import { getLocalizedError } from '../i18n/errorCopy';
import { translateSessionMode, translateSessionName } from '../i18n/sessionCopy';
import {
  canEnterRoom,
  formatDateTime,
  getProfile,
  hoursUntil,
  isFutureSession,
  parseSessionStart,
  requestCancelSession,
  saveReview,
  subscribeUserSessions,
  toNumber,
} from '../services/marketplace';
import '../style/UserDashboard.css';

function getStatus(session, t) {
  if (session.status === 'cancelled' || session.status === 'refunded' || session.cancelAccepted || session.cancelledByTeller || session.cancelled) {
    return { key: 'cancelled', label: session.status === 'refunded' ? t('workflow.dashboard.refunded') : t('workflow.dashboard.cancelled') };
  }
  if (session.cancelStatus === 'accepted') return { key: 'cancelled', label: t('workflow.dashboard.cancelAccepted') };
  if (session.cancelStatus === 'pending' || session.cancelRequested || session.cancelRequest) return { key: 'cancellation-pending', label: t('workflow.dashboard.cancelPending') };
  if (session.status === 'completed' || session.completed) return { key: 'completed', label: t('workflow.dashboard.completed') };
  if (session.status === 'active') return { key: 'active', label: t('workflow.dashboard.live') };
  if (session.status === 'pending' || session.status === 'requested') return { key: 'pending', label: t('workflow.dashboard.pending') };
  if (session.status === 'paid' || session.status === 'confirmed') return { key: 'booked', label: t('workflow.dashboard.confirmed') };
  return { key: 'booked', label: t('workflow.dashboard.booked') };
}

function roomSessionId(session) {
  return session.marketplaceSessionId || (session.source === 'sessions' ? session.id : null);
}

function sessionTimestamp(session) {
  const start = parseSessionStart(session);
  if (start && !Number.isNaN(start.getTime())) return start.getTime();
  if (session.createdAt?.toMillis) return session.createdAt.toMillis();
  return new Date(session.createdAt || 0).getTime() || 0;
}

const UserDashboard = () => {
  const { user, userType, loading: authLoading } = useContext(AuthContext);
  const { t, language } = useI18n();
  const copy = pickPageCopy(language, clientDashboardCopy);
  const views = [
    { id: 'overview', label: t('workflow.dashboard.overview') },
    { id: 'sessions', label: t('workflow.dashboard.sessions') },
    { id: 'payments', label: t('workflow.dashboard.payments') },
    { id: 'pro', label: t('workflow.dashboard.pro') },
    { id: 'profile', label: t('workflow.dashboard.profile') },
  ];
  const isAdmin = userType === 'admin'
    || String(user?.email || '').trim().toLowerCase() === ADMIN_EMAIL;
  const [profile, setProfile] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [activeView, setActiveView] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [savingProfile, setSavingProfile] = useState(false);
  const [busySession, setBusySession] = useState('');
  const [feedback, setFeedback] = useState('');
  const [error, setError] = useState('');
  const [reviewSession, setReviewSession] = useState(null);
  const [reviewForm, setReviewForm] = useState({ rating: 5, comment: '' });
  const [profileForm, setProfileForm] = useState({
    name: '',
    description: '',
    profileImage: '',
    birthDate: '',
    birthTime: '',
    birthPlace: '',
    preferredTopics: '',
    readingGoal: '',
    privateNotes: '',
  });

  useEffect(() => {
    if (!user) {
      setProfile(null);
      return;
    }
    getProfile(user.uid)
      .then((data) => {
        const nextProfile = data || {};
        setProfile(nextProfile);
        setProfileForm({
          name: nextProfile.name || user.displayName || '',
          description: nextProfile.description || '',
          profileImage: nextProfile.profileImage || user.photoURL || '',
          birthDate: nextProfile.birthDate || '',
          birthTime: nextProfile.birthTime || '',
          birthPlace: nextProfile.birthPlace || '',
          preferredTopics: nextProfile.preferredTopics || '',
          readingGoal: nextProfile.readingGoal || '',
          privateNotes: nextProfile.privateNotes || '',
        });
      })
      .catch((err) => setError(getLocalizedError(err, language, copy.profileError)));
  }, [copy.profileError, language, user]);

  useEffect(() => {
    if (!user) {
      setSessions([]);
      setLoading(false);
      return undefined;
    }
    setLoading(true);
    setError('');
    return subscribeUserSessions(
      user.uid,
      (rows) => {
        setSessions(rows);
        setLoading(false);
      },
      (err) => {
        setError(getLocalizedError(err, language, copy.profileError));
        setLoading(false);
      },
    );
  }, [copy.profileError, language, user]);

  const orderedSessions = useMemo(() => [...sessions].sort((a, b) => {
    return sessionTimestamp(a) - sessionTimestamp(b);
  }), [sessions]);

  const upcoming = useMemo(() => orderedSessions.filter((session) => {
    const status = getStatus(session, t).key;
    return !['cancelled', 'completed'].includes(status) && isFutureSession(session);
  }), [orderedSessions, t]);

  const history = useMemo(() => orderedSessions
    .filter((session) => !upcoming.includes(session))
    .sort((a, b) => sessionTimestamp(b) - sessionTimestamp(a)), [orderedSessions, upcoming]);

  const nextSession = upcoming[0] || null;

  const handleCancel = async (session) => {
    setFeedback('');
    setError('');
    setBusySession(session.id);
    try {
      await requestCancelSession(session, 'client');
      setFeedback(t('workflow.dashboard.waitingApproval'));
    } catch (err) {
      setError(getLocalizedError(err, language, copy.cancelError));
    } finally {
      setBusySession('');
    }
  };

  const handleProfileSave = async (event) => {
    event.preventDefault();
    setSavingProfile(true);
    setFeedback('');
    setError('');
    try {
      await updateDoc(doc(db, 'users', user.uid), {
        name: profileForm.name.trim(),
        description: profileForm.description.trim(),
        profileImage: profileForm.profileImage.trim(),
        birthDate: profileForm.birthDate,
        birthTime: profileForm.birthTime,
        birthPlace: profileForm.birthPlace.trim(),
        preferredTopics: profileForm.preferredTopics.trim(),
        readingGoal: profileForm.readingGoal.trim(),
        privateNotes: profileForm.privateNotes.trim(),
        updatedAt: new Date().toISOString(),
      });
      setProfile((current) => ({ ...current, ...profileForm }));
      setFeedback(t('workflow.dashboard.save'));
    } catch (err) {
      setError(getLocalizedError(err, language, copy.profileError));
    } finally {
      setSavingProfile(false);
    }
  };

  const handleReviewSubmit = async (event) => {
    event.preventDefault();
    setError('');
    setFeedback('');
    try {
      await saveReview({ session: reviewSession, user, rating: reviewForm.rating, comment: reviewForm.comment });
      setReviewSession(null);
      setReviewForm({ rating: 5, comment: '' });
      setFeedback(copy.reviewSaved);
    } catch (err) {
      setError(getLocalizedError(err, language, copy.reviewError));
    }
  };

  const renderSessionActions = (session) => {
    const status = getStatus(session, t);
    const roomAccess = canEnterRoom(session);
    const targetId = roomSessionId(session);
    const paymentId = session.marketplaceSessionId || (session.source === 'sessions' ? session.id : null);
    const canRequestCancel = ['booked', 'pending'].includes(status.key) && hoursUntil(session) >= 24;

    return (
      <div className="session-actions">
        {status.key === 'pending' && paymentId && (
          <Link className="dashboard-button compact" to={`/session-payment/${paymentId}`}>{t('workflow.dashboard.showQr')}</Link>
        )}
        {!['cancelled', 'completed', 'pending'].includes(status.key) && targetId && (
          roomAccess.ok
            ? <Link className="dashboard-button compact" to={`/session/${targetId}`}>{t('workflow.dashboard.connect')}</Link>
            : <span className="action-note">{roomAccess.label}</span>
        )}
        {canRequestCancel && (
          <button
            className="dashboard-button compact ghost"
            type="button"
            disabled={busySession === session.id}
            onClick={() => handleCancel(session)}
          >
            {busySession === session.id ? t('workflow.dashboard.sending') : t('workflow.dashboard.requestCancel')}
          </button>
        )}
        {status.key === 'cancellation-pending' && <span className="action-note pending-note">{t('workflow.dashboard.waitingApproval')}</span>}
        {['booked', 'pending'].includes(status.key) && hoursUntil(session) < 24 && <span className="action-note">{t('workflow.dashboard.cancelClosed')}</span>}
        {status.key === 'completed' && !session.reviewedAt && (
          <button className="dashboard-button compact ghost" type="button" onClick={() => setReviewSession(session)}>{t('workflow.dashboard.leaveReview')}</button>
        )}
      </div>
    );
  };

  const renderSessionList = (rows, emptyText) => {
    if (loading) return <div className="dashboard-empty">{t('workflow.dashboard.loadingSessions')}</div>;
    if (!rows.length) return <div className="dashboard-empty"><strong>{t('workflow.dashboard.noSessions')}</strong><span>{emptyText}</span><Link to="/find-teller">{t('workflow.dashboard.browse')}</Link></div>;

    return (
      <div className="responsive-table-wrap">
        <table className="sessions-table">
          <thead><tr><th>{t('workflow.dashboard.reading')}</th><th>{t('workflow.dashboard.teller')}</th><th>{t('workflow.dashboard.date')}</th><th>{t('workflow.dashboard.length')}</th><th>{t('workflow.dashboard.price')}</th><th>{t('workflow.dashboard.status')}</th><th>{t('workflow.dashboard.actions')}</th></tr></thead>
          <tbody>{rows.map((session) => {
            const status = getStatus(session, t);
            return (
              <tr key={`${session.source || 'session'}-${session.id}`}>
                <td data-label={t('workflow.dashboard.reading')}><strong>{translateSessionName(session.sessionName, t, t('workflow.dashboard.liveReading'))}</strong><small>{translateSessionMode(session.sessionType || 'video', t)}</small></td>
                <td data-label={t('workflow.dashboard.teller')}>{session.tellerName || session.tutorName || session.tellerEmail || copy.tellerFallback}</td>
                <td data-label={t('workflow.dashboard.date')}>{formatDateTime(session)}</td>
                <td data-label={t('workflow.dashboard.length')}>{session.duration || 60} {copy.minuteShort}</td>
                <td data-label={t('workflow.dashboard.price')}>EUR {toNumber(session.price).toFixed(2)}</td>
                <td data-label={t('workflow.dashboard.status')}><span className={`status-pill status-${status.key}`}>{status.label}</span></td>
                <td data-label={t('workflow.dashboard.actions')}>{renderSessionActions(session)}</td>
              </tr>
            );
          })}</tbody>
        </table>
      </div>
    );
  };

  const renderCompactSessionList = (rows, emptyText) => {
    if (loading) return <div className="dashboard-empty">{t('workflow.dashboard.loadingSessions')}</div>;
    if (!rows.length) return <div className="dashboard-empty"><strong>{t('workflow.dashboard.noSessions')}</strong><span>{emptyText}</span><Link to="/find-teller">{t('workflow.dashboard.browse')}</Link></div>;

    return (
      <div className="next-session-list">
        {rows.map((session) => {
          const status = getStatus(session, t);
          return (
            <article className="next-session-card" key={`next-${session.source || 'session'}-${session.id}`}>
              <div className="next-session-copy">
                <span className="next-session-type">{copy[session.sessionType] || copy.video} {copy.readingSuffix}</span>
                <h3>{translateSessionName(session.sessionName, t, t('workflow.dashboard.liveReading'))}</h3>
                <div className="next-session-meta">
                  <span>{formatDateTime(session)}</span>
                  <span>{session.duration || 60} {copy.minuteShort}</span>
                  <span>EUR {toNumber(session.price).toFixed(2)}</span>
                </div>
                <p>{t('workflow.dashboard.with')} {session.tellerName || session.tutorName || session.tellerEmail || copy.tellerFallback}</p>
              </div>
              <span className={`status-pill status-${status.key}`}>{status.label}</span>
              <div className="next-session-actions">{renderSessionActions(session)}</div>
            </article>
          );
        })}
      </div>
    );
  };

  if (authLoading) return <><Header /><main className="user-dashboard-shell"><div className="dashboard-empty">{t('workflow.dashboard.loadingAccount')}</div></main><Footer /></>;

  if (isAdmin) return <Navigate to="/admin-login" replace />;

  if (!user) {
    return <><Header /><main className="user-dashboard-shell"><section className="dashboard-hero-card"><div><span className="eyebrow">{t('workflow.dashboard.clientDashboard')}</span><h1>{t('workflow.dashboard.signedOutTitle')}</h1><p>{t('workflow.dashboard.signedOutBody')}</p></div><div className="dashboard-actions-row"><Link className="dashboard-button" to="/login">{t('nav.login')}</Link><Link className="dashboard-button ghost" to="/register">{t('nav.create')}</Link></div></section></main><Footer /></>;
  }

  return (
    <div className="dashboard-screen">
      <Header />
      <main className="user-dashboard-shell">
        <section className="dashboard-hero-card">
          <div>
            <span className="eyebrow">{t('workflow.dashboard.clientSpace')}</span>
            <h1>{t('workflow.dashboard.welcome')}, {profile?.name || user.email?.split('@')[0]}.</h1>
            <p>{t('workflow.dashboard.heroBody')}</p>
          </div>
          <div className="dashboard-actions-row">
            <Link className="dashboard-button" to="/find-teller">{t('workflow.dashboard.bookReading')}</Link>
            <button className="dashboard-button pro-dashboard-button" type="button" onClick={() => setActiveView('pro')}>{t('workflow.dashboard.managePro')}</button>
            {nextSession && roomSessionId(nextSession) && canEnterRoom(nextSession).ok && <Link className="dashboard-button ghost" to={`/session/${roomSessionId(nextSession)}`}>{t('workflow.dashboard.joinNext')}</Link>}
          </div>
        </section>

        <nav className="dashboard-tabs" aria-label={t('a11y.dashboardSections')}>
          {views.map((view) => <button key={view.id} type="button" className={activeView === view.id ? 'active' : ''} onClick={() => setActiveView(view.id)}>{view.label}</button>)}
        </nav>

        {(error || feedback) && <div className={`dashboard-message ${error ? 'error' : 'success'}`} role="status">{error || feedback}</div>}

        {activeView === 'overview' && (
          <>
            <section className="dashboard-stat-grid">
              <article><span>{t('workflow.dashboard.upcoming')}</span><strong>{upcoming.length}</strong><p>{t('workflow.dashboard.scheduled')}</p></article>
              <article><span>{t('workflow.dashboard.completed')}</span><strong>{history.filter((item) => getStatus(item, t).key === 'completed').length}</strong><p>{t('workflow.dashboard.pastReadings')}</p></article>
              <article><span>{t('workflow.dashboard.payments')}</span><strong>{upcoming.filter((item) => getStatus(item, t).key === 'pending').length}</strong><p>{t('workflow.dashboard.awaitingConfirmation')}</p></article>
              <article><span>{t('workflow.dashboard.account')}</span><strong>{profile?.name || t('workflow.dashboard.client')}</strong><p>{user.email}</p></article>
            </section>
            <section className="dashboard-grid-two">
              <article className="dashboard-panel">
                <div className="panel-heading"><div><span className="eyebrow">{t('workflow.dashboard.comingUp')}</span><h2>{t('workflow.dashboard.nextSessions')}</h2></div><button type="button" className="text-button" onClick={() => setActiveView('sessions')}>{t('workflow.dashboard.viewAll')}</button></div>
                {renderCompactSessionList(upcoming.slice(0, 3), copy.reserveEmpty)}
              </article>
              <aside className="dashboard-panel dashboard-side-panel">
                <span className="eyebrow">{t('workflow.dashboard.quick')}</span>
                <h2>{t('workflow.dashboard.ready')}</h2>
                <Link to="/find-teller">{t('workflow.dashboard.find')}</Link>
                <button type="button" onClick={() => setActiveView('payments')}>{t('workflow.dashboard.viewPayments')}</button>
                <button type="button" onClick={() => setActiveView('pro')}>{t('workflow.dashboard.getPro')}</button>
                <Link to="/how-it-works">{t('workflow.dashboard.howSessions')}</Link>
                <Link to="/contact">{t('workflow.dashboard.contactSupport')}</Link>
              </aside>
            </section>
            <section className="dashboard-panel">
              <div className="panel-heading"><div><span className="eyebrow">{t('workflow.dashboard.recent')}</span><h2>{t('workflow.dashboard.history')}</h2></div><span>{history.length} {t('workflow.dashboard.records')}</span></div>
              {renderSessionList(history.slice(0, 5), copy.historyEmpty)}
            </section>
          </>
        )}

        {activeView === 'sessions' && (
          <div className="dashboard-section-stack">
            <section className="dashboard-panel"><div className="panel-heading"><div><span className="eyebrow">{t('workflow.dashboard.schedule')}</span><h2>{t('workflow.dashboard.upcomingSessions')}</h2></div><Link to="/find-teller">{t('workflow.dashboard.bookAnother')}</Link></div>{renderSessionList(upcoming, t('workflow.dashboard.scheduled'))}</section>
            <section className="dashboard-panel"><div className="panel-heading"><div><span className="eyebrow">{t('workflow.dashboard.archive')}</span><h2>{t('workflow.dashboard.pastCancelled')}</h2></div><span>{history.length} {t('workflow.dashboard.records')}</span></div>{renderSessionList(history, t('workflow.dashboard.pastReadings'))}</section>
          </div>
        )}

        {activeView === 'payments' && (
          <section className="dashboard-section-stack">
            <article className="dashboard-panel">
              <div className="panel-heading"><div><span className="eyebrow">{t('workflow.dashboard.directPayment')}</span><h2>{t('workflow.dashboard.paymentTitle')}</h2></div></div>
              <p>{t('workflow.dashboard.paymentBody')}</p>
            </article>
            <section className="dashboard-panel">
              <div className="panel-heading"><div><span className="eyebrow">{t('workflow.dashboard.awaitingPayment')}</span><h2>{t('workflow.dashboard.pendingBookings')}</h2></div></div>
              {renderSessionList(upcoming.filter((item) => getStatus(item, t).key === 'pending'), t('workflow.dashboard.awaitingPayment'))}
            </section>
          </section>
        )}

        {activeView === 'pro' && (
          <UserProPanel user={user} initialPlan={profile?.subscriptionPlan || 'free'} />
        )}

        {activeView === 'profile' && (
          <section className="dashboard-grid-two profile-grid">
            <form className="dashboard-panel profile-form" onSubmit={handleProfileSave}>
              <div className="panel-heading"><div><span className="eyebrow">{t('workflow.dashboard.accountDetails')}</span><h2>{t('workflow.dashboard.yourInfo')}</h2></div></div>
              <label>{t('workflow.dashboard.fullName')}<input value={profileForm.name} onChange={(event) => setProfileForm({ ...profileForm, name: event.target.value })} required /></label>
              <label>{copy.email}<input value={user.email || ''} disabled /></label>
              <label>{t('workflow.dashboard.about')}<textarea value={profileForm.description} onChange={(event) => setProfileForm({ ...profileForm, description: event.target.value })} placeholder={t('workflow.dashboard.aboutPlaceholder')} /></label>
              <ProfileImageUpload
                userId={user.uid}
                value={profileForm.profileImage}
                onChange={(downloadUrl) => setProfileForm({ ...profileForm, profileImage: downloadUrl })}
              />
              {profile?.subscriptionPlan === 'pro' && (
                <fieldset className="pro-profile-fields">
                  <legend>{copy.proProfile}</legend>
                  <label>{copy.birthDate}<input type="date" value={profileForm.birthDate} onChange={(event) => setProfileForm({ ...profileForm, birthDate: event.target.value })} /></label>
                  <label>{copy.birthTime}<input type="time" value={profileForm.birthTime} onChange={(event) => setProfileForm({ ...profileForm, birthTime: event.target.value })} /></label>
                  <label>{copy.birthPlace}<input value={profileForm.birthPlace} onChange={(event) => setProfileForm({ ...profileForm, birthPlace: event.target.value })} /></label>
                  <label>{copy.preferredTopics}<input value={profileForm.preferredTopics} onChange={(event) => setProfileForm({ ...profileForm, preferredTopics: event.target.value })} placeholder={copy.topicsPlaceholder} /></label>
                  <label>{copy.readingGoal}<textarea value={profileForm.readingGoal} onChange={(event) => setProfileForm({ ...profileForm, readingGoal: event.target.value })} /></label>
                  <label>{copy.privateNotes}<textarea value={profileForm.privateNotes} onChange={(event) => setProfileForm({ ...profileForm, privateNotes: event.target.value })} placeholder={copy.notesPlaceholder} /></label>
                </fieldset>
              )}
              <button className="dashboard-button" type="submit" disabled={savingProfile}>{savingProfile ? t('workflow.dashboard.saving') : t('workflow.dashboard.save')}</button>
            </form>
            <aside className="dashboard-panel account-preview">
              <div className="account-avatar">{profileForm.profileImage ? <img src={profileForm.profileImage} alt={copy.profileAlt} /> : <span>{(profileForm.name || user.email || 'F').charAt(0).toUpperCase()}</span>}</div>
              <h2>{profileForm.name || 'Fate Room client'}</h2><p>{user.email}</p><p>{profileForm.description || t('workflow.dashboard.heroBody')}</p><Link to="/privacy">{t('workflow.dashboard.privacy')}</Link>
            </aside>
          </section>
        )}

        {reviewSession && (
          <div className="dashboard-modal-backdrop" role="dialog" aria-modal="true" aria-labelledby="review-title">
            <form className="dashboard-review-modal" onSubmit={handleReviewSubmit}>
              <span className="eyebrow">{t('workflow.dashboard.review')}</span><h2 id="review-title">{t('workflow.dashboard.reviewTitle')}</h2>
              <label>{t('workflow.dashboard.rating')}<select value={reviewForm.rating} onChange={(event) => setReviewForm({ ...reviewForm, rating: event.target.value })}><option value="5">5</option><option value="4">4</option><option value="3">3</option><option value="2">2</option><option value="1">1</option></select></label>
              <label>{t('workflow.dashboard.comment')}<textarea value={reviewForm.comment} onChange={(event) => setReviewForm({ ...reviewForm, comment: event.target.value })} required /></label>
              <div className="dashboard-actions-row"><button className="dashboard-button" type="submit">{t('workflow.dashboard.saveReview')}</button><button className="dashboard-button ghost" type="button" onClick={() => setReviewSession(null)}>{t('workflow.dashboard.cancel')}</button></div>
            </form>
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
};

export default UserDashboard;
